import { z } from 'zod';
import type { LocaleObject } from '@nuxtjs/i18n';
import enLanguages from '@cospired/i18n-iso-languages/langs/en.json';
import deLanguages from '@cospired/i18n-iso-languages/langs/de.json';
import frLanguages from '@cospired/i18n-iso-languages/langs/fr.json';
import { match } from 'ts-pattern';
import { exhaustive } from '../types/Exhaustive';

export type I18nLocale = LocaleObject['code'];

export const LOCALES = exhaustive<I18nLocale>()(['en', 'de', 'fr']);

export const localesSchema = z.enum(LOCALES);

export const getLanguagesFileByLocale = (locale: I18nLocale) => {
	return match(locale)
		.with('de', () => deLanguages)
		.with('en', () => enLanguages)
		.with('fr', () => frLanguages)
		.exhaustive();
};

export const getLanguageNamesForTargetLocale = (targetLocale: I18nLocale): Record<I18nLocale, string> => {
	const languages = getLanguagesFileByLocale(targetLocale).languages;

	return LOCALES.reduce((record, locale) => ({
		...record,
		[locale]: locale in languages ? languages[locale] : locale,
	}), {} as Record<I18nLocale, string>);
};

export const localeDescription: Record<I18nLocale, Record<I18nLocale, string>> = LOCALES.reduce((record, locale) => ({
	...record,
	[locale]: getLanguageNamesForTargetLocale(locale),
}), {} as Record<I18nLocale, Record<I18nLocale, string>>);

export const getLocaleDescriptions = (locale: I18nLocale) => {
	return localeDescription[locale];
};

export const getLocaleDescription = (locale: I18nLocale) => {
	return getLocaleDescriptions(locale)[locale];
};
